import { GenericServiceBase } from "../generic-service";
import { paramsDoFiltro, type MapaAnomaliasTrilha, type RotuloComAjuda } from "../trilha/trilha.service";
import type { IMetaOnboardingLog, MetaOnboardingFluxo, MetaOnboardingStatus } from "./meta-trilha.service";

/**
 * Onboarding Meta por EXECUÇÃO (T-29): uma linha por conexão/desconexão, com
 * situação, desfecho, anomalias e retenção. Espelha `meta-onboarding.constants.ts`
 * e `MetaOnboardingConsultaService` do servidor — ao mexer lá, mexa aqui.
 */

export type StatusExecucaoOnboarding = 'EM_ANDAMENTO' | 'CONCLUIDA' | 'FALHOU' | 'ABANDONADA';
export type OrigemExecucaoOnboarding = 'ADMIN' | 'USUARIO' | 'META' | 'SISTEMA';
export type DesfechoOnboarding = 'CONECTADO' | 'DESCONECTADO' | 'RECUSADO' | 'FALHA' | 'ABANDONADA' | 'EXECUTADA';

export const TIPOS_ANOMALIA_ONBOARDING = [
    'ABANDONADA', 'TOKEN_EXCHANGE_FALHOU', 'REGISTRO_NUMERO_FALHOU', 'SEM_WEBHOOK_APOS_CONEXAO',
    'ASSINATURA_WEBHOOKS_FALHOU', 'GATEWAY_FALHOU', 'PARTNER_REMOVED',
] as const;

export interface IOnboardingExecucao {
    id: string;
    fluxo: MetaOnboardingFluxo;
    origem: OrigemExecucaoOnboarding;
    status: StatusExecucaoOnboarding;
    desfecho: DesfechoOnboarding | null;
    desfechoResumo: string | null;
    etapaDecisiva: string | null;
    idInbox: string | null;
    nomeInbox: string | null;
    idConta: string | null;
    nomeConta: string | null;
    idUsuario: string | null;
    nomeUsuario: string | null;
    idAdministrador: string | null;
    wabaId: string | null;
    phoneNumberId: string | null;
    businessId: string | null;
    pageId: string | null;
    igId: string | null;
    numero: string | null;
    idExecucaoPai: string | null;
    idWebhookOrigem: string | null;
    totalEtapas: number;
    totalFalhas: number;
    ultimoErroCode: number | null;
    anomaliasJson: MapaAnomaliasTrilha | null;
    anomalias: string[];
    temAnomalia: boolean;
    webhookVerificado: boolean;
    primeiroWebhookEm: string | null;
    iniciadoEm: string;
    ultimaAtividadeEm: string;
    encerradoEm: string | null;
    preservado: boolean;
    expiraEm: string;
}

/** Webhook resumido do painel "Depois da conexão". */
export interface IWebhookDepois {
    id: string;
    canal: string;
    campo: string | null;
    ehMensagem: boolean;
    recebidoEm: string;
    assinaturaValida: boolean | null;
}

export interface IPaginaExecucoesOnboarding {
    total: number;
    itens: IOnboardingExecucao[];
    ttlDias: number;
    ttlAnomaliaDias: number;
}

export interface IResumoExecucoesOnboarding {
    porStatus: { status: StatusExecucaoOnboarding; total: number }[];
    porFluxo: { fluxo: MetaOnboardingFluxo; total: number }[];
    porAnomalia: { tipo: string; total: number }[];
}

export interface IDetalheExecucaoOnboarding {
    execucao: IOnboardingExecucao;
    etapas: IMetaOnboardingLog[];
    webhooks: IWebhookDepois[];
    ttlDias: number;
    ttlAnomaliaDias: number;
}

/** Espelho do `ConsultarOnboardingExecucoesDto`. */
export class OnboardingExecucaoFiltro {
    fluxo?: MetaOnboardingFluxo[];
    status?: StatusExecucaoOnboarding[];
    origem?: OrigemExecucaoOnboarding[];
    anomalia?: string[];
    temAnomalia?: boolean;
    preservado?: boolean;
    idExecucao?: string;
    idInbox?: string;
    idConta?: string;
    idUsuario?: string;
    wabaId?: string;
    phoneNumberId?: string;
    pageId?: string;
    igId?: string;
    /** String para casar com o seletor; o servidor converte. */
    erroCode?: string;
    busca?: string;
    desde?: string;
    ate?: string;
    pagina = 1;
    limite = 50;
}

/** Campos que viajam na URL (permalink da lista e filtros salvos). */
export const CHAVES_FILTRO_URL_ONBOARDING = [
    'fluxo', 'status', 'origem', 'anomalia', 'temAnomalia', 'preservado', 'idExecucao', 'idInbox', 'idConta',
    'idUsuario', 'wabaId', 'phoneNumberId', 'pageId', 'igId', 'erroCode', 'busca', 'desde', 'ate',
] as const;

/** Filtro da lista de ETAPAS (aba "Etapas (cru)") — `fluxo`/`status` aceitam vários (F-43). */
export class OnboardingEtapaFiltro {
    fluxo?: MetaOnboardingFluxo[];
    status?: MetaOnboardingStatus[];
    etapa?: string;
    idExecucao?: string;
    idInbox?: string;
    idConta?: string;
    idUsuario?: string;
    wabaId?: string;
    phoneNumberId?: string;
    pageId?: string;
    igId?: string;
    erroCode?: string;
    preservado?: boolean;
    busca?: string;
    desde?: string;
    ate?: string;
    pagina = 1;
    limite = 50;
}

export class MetaOnboardingExecucoesService extends GenericServiceBase {
    constructor() { super('admin/meta-trilha') }

    async listar(filtro: OnboardingExecucaoFiltro): Promise<IPaginaExecucoesOnboarding> {
        return await this.f.get('/onboarding/execucoes', paramsDoFiltro(filtro)) as IPaginaExecucoesOnboarding;
    }

    async resumo(filtro: OnboardingExecucaoFiltro): Promise<IResumoExecucoesOnboarding> {
        const { pagina: _p, limite: _l, ...resto } = filtro;
        return await this.f.get('/onboarding/execucoes/resumo', paramsDoFiltro(resto)) as IResumoExecucoesOnboarding;
    }

    async detalhe(id: string): Promise<IDetalheExecucaoOnboarding> {
        return await this.f.get(`/onboarding/execucoes/${id}`) as IDetalheExecucaoOnboarding;
    }

    async preservar(ids: string[], preservado: boolean): Promise<{ atualizadas: number; etapas: number }> {
        return await this.f.post('/onboarding/execucoes/preservar', { ids, preservado }) as { atualizadas: number; etapas: number };
    }

    async marcarAnomalia(id: string, tipo: string, valor: boolean | null, nota?: string): Promise<IOnboardingExecucao> {
        return await this.f.post(`/onboarding/execucoes/${id}/anomalia`, { tipo, valor, ...(nota ? { nota } : {}) }) as IOnboardingExecucao;
    }

    async varrer(): Promise<Record<string, unknown>> {
        return await this.f.post('/onboarding/varrer', {}) as Record<string, unknown>;
    }

    async purgar(): Promise<Record<string, unknown>> {
        return await this.f.post('/purgar', {}) as Record<string, unknown>;
    }

    async listarEtapas(filtro: OnboardingEtapaFiltro): Promise<{ total: number; itens: IMetaOnboardingLog[]; ttlDias: number }> {
        return await this.f.get('/onboarding', paramsDoFiltro(filtro)) as { total: number; itens: IMetaOnboardingLog[]; ttlDias: number };
    }
}

// ─────────────────────────────── Rótulos PT-BR ───────────────────────────────

export const ROTULO_STATUS_EXECUCAO: Record<StatusExecucaoOnboarding, { rotulo: string; tom: string; ajuda: string }> = {
    EM_ANDAMENTO: { rotulo: 'Em andamento', tom: 'blue', ajuda: 'A conexão ainda não chegou a uma etapa final.' },
    CONCLUIDA: { rotulo: 'Concluída', tom: 'green', ajuda: 'Chegou à etapa final (inbox criada/vinculada, ou desvínculo feito).' },
    FALHOU: { rotulo: 'Falhou', tom: 'red', ajuda: 'Uma etapa falhou e nenhuma tentativa seguinte a concluiu.' },
    ABANDONADA: { rotulo: 'Abandonada', tom: 'amber', ajuda: 'Parada há mais de 30 min sem chegar ao fim (diálogo fechado, retorno que não veio).' },
};

export const ROTULO_ORIGEM_ONBOARDING: Record<OrigemExecucaoOnboarding, string> = {
    ADMIN: 'Admin',
    USUARIO: 'Cliente',
    META: 'Meta (aviso)',
    SISTEMA: 'Sistema',
};

export const ROTULO_DESFECHO_ONBOARDING: Record<DesfechoOnboarding, string> = {
    CONECTADO: 'Conectado',
    DESCONECTADO: 'Desconectado',
    RECUSADO: 'Recusado pelo cliente',
    FALHA: 'Falha',
    ABANDONADA: 'Abandonada',
    EXECUTADA: 'Executada (console da Graph)',
};

export const ROTULO_STATUS_ETAPA: Record<MetaOnboardingStatus, { rotulo: string; tom: string; icone: string }> = {
    INICIADA: { rotulo: 'Iniciada', tom: 'slate', icone: 'ri-loader-4-line' },
    SUCESSO: { rotulo: 'Sucesso', tom: 'green', icone: 'ri-check-line' },
    FALHA: { rotulo: 'Falha', tom: 'red', icone: 'ri-close-circle-line' },
};

const ROTULO_ANOMALIA_FIXA: Record<(typeof TIPOS_ANOMALIA_ONBOARDING)[number], RotuloComAjuda> = {
    ABANDONADA: { rotulo: 'Abandonada', icone: 'ri-time-line', ajuda: 'Ficou parada sem chegar a uma etapa final.' },
    TOKEN_EXCHANGE_FALHOU: { rotulo: 'Troca do code falhou', icone: 'ri-key-2-line', ajuda: 'A Meta recusou trocar o code do diálogo por token (code expirado, redirect_uri diferente…).' },
    REGISTRO_NUMERO_FALHOU: { rotulo: 'Registro do número falhou', icone: 'ri-phone-lock-line', ajuda: 'POST /register na Cloud API falhou — a inbox nasce, mas o número não envia até registrar.' },
    SEM_WEBHOOK_APOS_CONEXAO: { rotulo: 'Sem webhook depois', icone: 'ri-signal-wifi-off-line', ajuda: 'Nenhum webhook chegou em 24 h depois da conexão: gateway, subscribed_apps ou app em modo desenvolvimento.' },
    ASSINATURA_WEBHOOKS_FALHOU: { rotulo: 'subscribed_apps falhou', icone: 'ri-notification-off-line', ajuda: 'A inscrição do app nos webhooks da WABA falhou.' },
    GATEWAY_FALHOU: { rotulo: 'Gateway falhou', icone: 'ri-route-line', ajuda: 'O registro da inbox no gateway falhou: os webhooks chegam ao gateway sem destino.' },
    PARTNER_REMOVED: { rotulo: 'Removido pelo cliente na Meta', icone: 'ri-link-unlink', ajuda: 'O cliente removeu o nosso app do Business Manager (PARTNER_REMOVED).' },
};

/** Rótulo de qualquer anomalia, inclusive `ERRO_META_<code>`. */
export function rotuloAnomaliaOnboarding(tipo: string): RotuloComAjuda {
    const fixa = (ROTULO_ANOMALIA_FIXA as Record<string, RotuloComAjuda>)[tipo];
    if (fixa) return fixa;
    const code = /^ERRO_META_(\d+)$/.exec(tipo)?.[1];
    if (code) return { rotulo: `Erro Meta ${code}`, icone: 'ri-error-warning-line', ajuda: `A Meta respondeu error.code ${code} numa etapa.` };
    return { rotulo: tipo, icone: 'ri-alarm-warning-line', ajuda: '' };
}

/** Mapa de rótulos para os tipos de um conjunto de execuções (os dinâmicos `ERRO_META_*` incluídos). */
export function rotulosDeAnomalias(tipos: Iterable<string>): Record<string, RotuloComAjuda> {
    const saida: Record<string, RotuloComAjuda> = { ...ROTULO_ANOMALIA_FIXA };
    for (const t of tipos) if (!saida[t]) saida[t] = rotuloAnomaliaOnboarding(t);
    return saida;
}

/** Nomes das etapas gravadas pelo servidor, em português. Desconhecida aparece crua. */
export const ROTULO_ETAPA: Record<string, string> = {
    'abrir-dialogo': 'Diálogo da Meta aberto',
    'dialogo-recusado': 'Diálogo recusado/cancelado',
    'callback': 'Retorno do OAuth',
    'token-exchange': 'Troca do code por token',
    'descobrir-waba': 'Descoberta da WABA',
    'descobrir-numero': 'Descoberta do número',
    'assinar-webhooks-waba': 'Inscrição nos webhooks da WABA',
    'registrar-numero': 'Registro do número (Cloud API)',
    'criar-inbox': 'Criação da inbox',
    'criar-inbox-admin': 'Criação da inbox (admin)',
    'criar-inbox-usuario': 'Criação da inbox (cliente)',
    'provisionar-conexao-inbox-existente': 'Conexão numa inbox existente',
    'conectar-inbox-existente': 'Conexão numa inbox existente',
    'criar-inbox-e-vincular': 'Criação da inbox e vínculo da Página/conta',
    'vincular-pagina': 'Vínculo da Página',
    'vincular-conta': 'Vínculo da conta Instagram',
    'converter-evolution-para-hibrido': 'Conversão Evolution → híbrido',
    'registrar-gateway': 'Registro no gateway',
    'template-boas-vindas': 'Template de boas-vindas',
    'recuperar-inbox': 'Recuperação de mensagens',
    'desvincular-pagina': 'Desvínculo da Página',
    'desvincular-conta': 'Desvínculo da conta Instagram',
    'descartar-metade-oficial': 'Remoção da conexão oficial',
    // WAME (modelo de contas novo da Meta)
    'wame-avaliar': 'WAME: avaliação do número',
    'wame-sondar-numero': 'WAME: leitura do número (webhook_configuration)',
    'wame-sondar-assinaturas': 'WAME: apps assinantes (subscribed_apps)',
    'wame-sondar-funding': 'WAME: forma de pagamento (primary_funding_id)',
    'wame-sondar-messaging-account': 'WAME: identidade da Messaging account',
    'wame-classificar': 'WAME: classificação (origem e modelo)',
    'wame-override-webhook-numero': 'WAME: override de webhook no número',
    'wame-aguardar-roteamento': 'WAME: aguardando Conversation Routing',
    'wame-roteamento-confirmado': 'WAME: roteamento confirmado',
    'wame-parceiro-adicionado': 'WAME: outro parceiro entrou (PARTNER_ADDED)',
    'wame-parceiro-removido': 'WAME: fomos removidos (PARTNER_REMOVED)',
    // Acompanhamento por operador (WAME fase 3)
    'aguardando-operador': 'Parada esperando o operador',
    'dialogo-erro': 'A Meta devolveu erro no diálogo',
    'encerrada-manualmente': 'Encerrada à mão por um admin',
    'operador-assumiu': 'Operador assumiu',
    'operador-expirou': 'Janela do operador expirou (segue com o padrão)',
    'operador-concluido': 'Roteiro concluído pelo operador',
    'operador-cancelou': 'Operador cancelou o acompanhamento',
    'capturar-proveniencia': 'Proveniência do número',
    'migrar-templates': 'Migração de templates',
    'sync-historico': 'Sincronização do histórico',
    'notificar-pronto': 'Aviso INBOX_PRONTO ao cliente',
};

export function rotuloEtapa(etapa: string): string {
    const [base, contexto] = etapa.split(':');
    const rotulo = ROTULO_ETAPA[base] ?? etapa;
    return contexto && ROTULO_ETAPA[base] ? `${rotulo} (${contexto})` : rotulo;
}
